import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  Calendar, Plus, Eye, CheckCircle, XCircle, Clock, Users, Building2,
  FileText, Search, ArrowUpRight, ArrowDownRight, Minus, Phone, MapPin,
  Edit, Trash2, CalendarClock, MoreHorizontal, Download
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';

const STATUS_COLORS = {
  "Agendada": "bg-blue-500",
  "Em Curso": "bg-amber-500",
  "Submetida": "bg-purple-500",
  "Aprovada": "bg-green-500",
  "Rejeitada": "bg-red-500",
  "Cancelada": "bg-slate-500"
};

export default function GestaoVisitasPage() {
  const [stats, setStats] = useState(null);
  const [visitas, setVisitas] = useState([]);
  const [visitadores, setVisitadores] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisita, setSelectedVisita] = useState(null);
  const [comparacao, setComparacao] = useState(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [aprovarModalOpen, setAprovarModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reagendarModalOpen, setReagendarModalOpen] = useState(false);
  
  // Form
  const [formData, setFormData] = useState({
    instituicao_id: '', visitador_id: '', formulario_id: '', data_agendada: '', hora_agendada: '', tipo_visita: 'Presencial', observacoes_agendamento: ''
  });
  const [aprovarData, setAprovarData] = useState({ aprovado: true, observacoes_gestor: '' });
  const [reagendarData, setReagendarData] = useState({ data_agendada: '', hora_agendada: '', motivo: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, visitasRes, visitadoresRes, formsRes, instRes] = await Promise.all([
        apiClient.get('/visitare/visitas/stats'),
        apiClient.get('/visitare/visitas'),
        apiClient.get('/visitare/visitadores'),
        apiClient.get('/visitare/formularios?ativo=true'),
        apiClient.get('/instituicoes?limit=500')
      ]);
      setStats(statsRes.data);
      setVisitas(visitasRes.data);
      setVisitadores(visitadoresRes.data);
      setFormularios(formsRes.data);
      setInstituicoes(instRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadVisitaDetail = async (visita) => {
    try {
      const [detailRes, compRes] = await Promise.all([
        apiClient.get(`/visitare/visitas/${visita.id}`),
        apiClient.get(`/visitare/visitas/${visita.id}/comparacao`)
      ]);
      setSelectedVisita(detailRes.data);
      setComparacao(compRes.data);
      setDetailModalOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleCreateVisita = async () => {
    if (!formData.instituicao_id || !formData.visitador_id || !formData.data_agendada) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      // formulario_id is optional - backend will auto-select based on tipo_visita
      const submitData = { ...formData };
      if (!submitData.formulario_id) {
        delete submitData.formulario_id; // Let backend auto-select
      }
      await apiClient.post('/visitare/visitas', submitData);
      toast.success('Visita agendada com sucesso');
      setCreateModalOpen(false);
      setFormData({ instituicao_id: '', visitador_id: '', formulario_id: '', data_agendada: '', hora_agendada: '', tipo_visita: 'Presencial', observacoes_agendamento: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao agendar visita');
    }
  };

  const handleAprovar = async () => {
    try {
      await apiClient.post(`/visitare/visitas/${selectedVisita.id}/aprovar`, aprovarData);
      toast.success(aprovarData.aprovado ? 'Visita aprovada' : 'Visita rejeitada');
      setAprovarModalOpen(false);
      setDetailModalOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao processar');
    }
  };

  const openEditModal = (visita) => {
    setSelectedVisita(visita);
    setFormData({
      instituicao_id: visita.instituicao_id || '',
      visitador_id: visita.visitador_id || '',
      formulario_id: visita.formulario_id || '',
      data_agendada: visita.data_agendada || '',
      hora_agendada: visita.hora_agendada || '',
      tipo_visita: visita.tipo_visita || 'Presencial',
      observacoes_agendamento: visita.observacoes_agendamento || ''
    });
    setEditModalOpen(true);
  };

  const handleEditVisita = async () => {
    if (!selectedVisita) return;
    try {
      const fd = new FormData();
      fd.append('instituicao_id', formData.instituicao_id);
      fd.append('visitador_id', formData.visitador_id);
      fd.append('formulario_id', formData.formulario_id);
      fd.append('data_agendada', formData.data_agendada);
      fd.append('hora_agendada', formData.hora_agendada || '');
      fd.append('tipo_visita', formData.tipo_visita);
      fd.append('observacoes_agendamento', formData.observacoes_agendamento || '');
      
      await apiClient.put(`/visitare/visitas/${selectedVisita.id}`, fd);
      toast.success('Visita atualizada com sucesso');
      setEditModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar visita');
    }
  };

  const handleCancelVisita = async () => {
    if (!selectedVisita) return;
    try {
      await apiClient.delete(`/visitare/visitas/${selectedVisita.id}`);
      toast.success('Visita cancelada com sucesso');
      setCancelModalOpen(false);
      setDetailModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar visita');
    }
  };

  const openReagendarModal = (visita) => {
    setSelectedVisita(visita);
    setReagendarData({
      data_agendada: '',
      hora_agendada: '',
      motivo: ''
    });
    setReagendarModalOpen(true);
  };

  const handleReagendarVisita = async () => {
    if (!selectedVisita || !reagendarData.data_agendada) {
      toast.error('Selecione uma nova data');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('data_agendada', reagendarData.data_agendada);
      fd.append('hora_agendada', reagendarData.hora_agendada || '');
      fd.append('motivo', reagendarData.motivo || '');
      
      await apiClient.put(`/visitare/visitas/${selectedVisita.id}/reagendar`, fd);
      toast.success('Visita reagendada com sucesso');
      setReagendarModalOpen(false);
      setDetailModalOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reagendar visita');
    }
  };

  const filteredVisitas = visitas.filter(v => {
    const matchesStatus = filterStatus === 'all' || v.status === filterStatus;
    const matchesSearch = !searchTerm || 
      v.instituicao_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.visitador_nome?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestao-visitas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Gestão de Visitas</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Portal Visitare - Agendar e aprovar visitas de avaliação</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="bg-[#0066CC] hover:bg-[#0052a3]">
          <Plus className="w-4 h-4 mr-2" />
          Agendar Visita
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats?.agendadas || 0}</p>
              <p className="text-xs text-slate-500">Agendadas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{stats?.em_curso || 0}</p>
              <p className="text-xs text-slate-500">Em Curso</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats?.submetidas || 0}</p>
              <p className="text-xs text-slate-500">Submetidas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats?.aprovadas || 0}</p>
              <p className="text-xs text-slate-500">Aprovadas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats?.rejeitadas || 0}</p>
              <p className="text-xs text-slate-500">Rejeitadas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">{stats?.presenciais || 0}</p>
              <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> Presenciais</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-teal-600">{stats?.telefonicas || 0}</p>
              <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Phone className="w-3 h-3" /> Telefónicas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#0066CC]">{stats?.score_medio || 0}</p>
              <p className="text-xs text-slate-500">Score Médio</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar instituição ou visitador..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos os status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Agendada">Agendadas</SelectItem>
                <SelectItem value="Em Curso">Em Curso</SelectItem>
                <SelectItem value="Submetida">Submetidas</SelectItem>
                <SelectItem value="Aprovada">Aprovadas</SelectItem>
                <SelectItem value="Rejeitada">Rejeitadas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instituição</TableHead>
                <TableHead>Visitador</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Formulário</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVisitas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    Nenhuma visita encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredVisitas.map(visita => (
                  <TableRow key={visita.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{visita.instituicao_nome}</span>
                      </div>
                    </TableCell>
                    <TableCell>{visita.visitador_nome}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {visita.data_agendada}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={visita.tipo_visita === 'Telefónica' ? 'border-teal-500 text-teal-600' : 'border-indigo-500 text-indigo-600'}>
                        {visita.tipo_visita === 'Telefónica' ? <Phone className="w-3 h-3 mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}
                        {visita.tipo_visita || 'Presencial'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{visita.formulario_nome}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {visita.score_final ? (
                        <span className="font-bold text-[#0066CC]">{visita.score_final}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[visita.status]} text-white`}>
                        {visita.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`visita-actions-${visita.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => loadVisitaDetail(visita)}>
                            <Eye className="w-4 h-4 mr-2" /> Ver Detalhes
                          </DropdownMenuItem>
                          {['Submetida', 'Aprovada'].includes(visita.status) && (
                            <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/visitare/visitas/${visita.id}/pdf`, '_blank')}>
                              <Download className="w-4 h-4 mr-2" /> Descarregar PDF
                            </DropdownMenuItem>
                          )}
                          {visita.status === 'Agendada' && (
                            <>
                              <DropdownMenuItem onClick={() => openEditModal(visita)}>
                                <Edit className="w-4 h-4 mr-2" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => { setSelectedVisita(visita); setCancelModalOpen(true); }} className="text-red-600">
                                <XCircle className="w-4 h-4 mr-2" /> Cancelar
                              </DropdownMenuItem>
                            </>
                          )}
                          {visita.status === 'Cancelada' && (
                            <DropdownMenuItem onClick={() => openReagendarModal(visita)}>
                              <CalendarClock className="w-4 h-4 mr-2" /> Reagendar
                            </DropdownMenuItem>
                          )}
                          {visita.status === 'Em Curso' && (
                            <DropdownMenuItem onClick={() => { setSelectedVisita(visita); setCancelModalOpen(true); }} className="text-red-600">
                              <XCircle className="w-4 h-4 mr-2" /> Cancelar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agendar Nova Visita</DialogTitle>
            <DialogDescription>Agende uma visita de avaliação a uma instituição</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Instituição *</Label>
              <Select value={formData.instituicao_id} onValueChange={v => setFormData(p => ({ ...p, instituicao_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visitador *</Label>
              <Select value={formData.visitador_id} onValueChange={v => setFormData(p => ({ ...p, visitador_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {visitadores.filter(a => a.status === 'ativo').map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Formulário <span className="text-slate-400 text-xs">(auto por tipo)</span></Label>
              <Select value={formData.formulario_id} onValueChange={v => setFormData(p => ({ ...p, formulario_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto-selecionar..." />
                </SelectTrigger>
                <SelectContent>
                  {formularios.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nome} ({f.tipo || 'Geral'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Visita *</Label>
              <Select value={formData.tipo_visita} onValueChange={v => setFormData(p => ({ ...p, tipo_visita: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Presencial">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Presencial
                    </div>
                  </SelectItem>
                  <SelectItem value="Telefónica">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Telefónica
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={formData.data_agendada} onChange={e => setFormData(p => ({ ...p, data_agendada: e.target.value }))} />
              </div>
              <div>
                <Label>Hora</Label>
                <Input type="time" value={formData.hora_agendada} onChange={e => setFormData(p => ({ ...p, hora_agendada: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={formData.observacoes_agendamento} onChange={e => setFormData(p => ({ ...p, observacoes_agendamento: e.target.value }))} placeholder="Notas para o visitador..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateVisita} className="bg-[#0066CC]">Agendar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Visita</DialogTitle>
            <DialogDescription>{selectedVisita?.instituicao_nome}</DialogDescription>
          </DialogHeader>
          
          {selectedVisita && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList>
                <TabsTrigger value="info">Informação</TabsTrigger>
                <TabsTrigger value="respostas">Respostas</TabsTrigger>
                <TabsTrigger value="comparacao">Comparação</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Visitador</p>
                    <p className="font-medium">{selectedVisita.visitador_nome}</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Data Agendada</p>
                    <p className="font-medium">{selectedVisita.data_agendada} {selectedVisita.hora_agendada}</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Tipo de Visita</p>
                    <Badge variant="outline" className={selectedVisita.tipo_visita === 'Telefónica' ? 'border-teal-500 text-teal-600' : 'border-indigo-500 text-indigo-600'}>
                      {selectedVisita.tipo_visita === 'Telefónica' ? <Phone className="w-3 h-3 mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}
                      {selectedVisita.tipo_visita || 'Presencial'}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={`${STATUS_COLORS[selectedVisita.status]} text-white`}>{selectedVisita.status}</Badge>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg col-span-2">
                    <p className="text-xs text-slate-500">Formulário</p>
                    <p className="font-medium">{selectedVisita.formulario_nome}</p>
                  </div>
                </div>

                {selectedVisita.score_final && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Score Final</span>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-[#0066CC]">{selectedVisita.score_final}</span>
                        <Badge variant="outline">{selectedVisita.classificacao}</Badge>
                      </div>
                    </div>
                    <Progress value={selectedVisita.score_final} className="h-3" />
                    
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {Object.entries(selectedVisita.scores_por_dimensao || {}).map(([dim, score]) => (
                        <div key={dim} className="text-center">
                          <p className="text-xs text-slate-500">{dim}</p>
                          <p className="font-bold">{score}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedVisita.observacoes_visitador && (
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Observações do Visitador</p>
                    <p className="text-sm">{selectedVisita.observacoes_visitador}</p>
                  </div>
                )}

                {selectedVisita.status === 'Submetida' && (
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => { setAprovarData({ aprovado: false, observacoes_gestor: '' }); setAprovarModalOpen(true); }} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-2" /> Rejeitar
                    </Button>
                    <Button onClick={() => { setAprovarData({ aprovado: true, observacoes_gestor: '' }); setAprovarModalOpen(true); }} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-2" /> Aprovar
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="respostas" className="space-y-3">
                {selectedVisita.formulario?.dimensoes?.map(dim => (
                  <div key={dim.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{dim.codigo} - {dim.nome}</span>
                      <Badge variant="outline">{selectedVisita.scores_por_dimensao?.[dim.codigo] || 0}</Badge>
                    </div>
                    <div className="space-y-2">
                      {dim.criterios?.map(crit => {
                        const resposta = selectedVisita.respostas?.find(r => r.criterio_id === crit.id);
                        return (
                          <div key={crit.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 dark:bg-slate-800 rounded">
                            <span className="flex-1">{crit.questao}</span>
                            <span className="font-medium ml-4">
                              {resposta?.resposta_valor !== undefined ? String(resposta.resposta_valor) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="comparacao">
                {!comparacao?.tem_anterior ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Não existe visita anterior para comparação</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span>Score Final</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">{comparacao.evolucao.score_final.anterior}</span>
                          <span className="text-xl font-bold">→</span>
                          <span className="text-xl font-bold text-[#0066CC]">{comparacao.evolucao.score_final.atual}</span>
                          {comparacao.evolucao.score_final.diferenca > 0 ? (
                            <Badge className="bg-green-500 text-white"><ArrowUpRight className="w-3 h-3" /> +{comparacao.evolucao.score_final.diferenca}</Badge>
                          ) : comparacao.evolucao.score_final.diferenca < 0 ? (
                            <Badge className="bg-red-500 text-white"><ArrowDownRight className="w-3 h-3" /> {comparacao.evolucao.score_final.diferenca}</Badge>
                          ) : (
                            <Badge variant="outline"><Minus className="w-3 h-3" /> 0</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {Object.entries(comparacao.evolucao.por_dimensao || {}).map(([dim, data]) => (
                        <div key={dim} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="font-medium">{dim}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">{data.anterior}</span>
                            <span>→</span>
                            <span className="font-bold">{data.atual}</span>
                            {data.diferenca > 0 ? (
                              <span className="text-green-600 text-sm">+{data.diferenca}</span>
                            ) : data.diferenca < 0 ? (
                              <span className="text-red-600 text-sm">{data.diferenca}</span>
                            ) : (
                              <span className="text-slate-400 text-sm">0</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprovar Modal */}
      <Dialog open={aprovarModalOpen} onOpenChange={setAprovarModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{aprovarData.aprovado ? 'Aprovar Visita' : 'Rejeitar Visita'}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Observações do Gestor</Label>
            <Textarea
              value={aprovarData.observacoes_gestor}
              onChange={e => setAprovarData(p => ({ ...p, observacoes_gestor: e.target.value }))}
              placeholder="Adicione comentários..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAprovarModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleAprovar} 
              className={aprovarData.aprovado ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {aprovarData.aprovado ? 'Aprovar' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Visita</DialogTitle>
            <DialogDescription>Alterar dados da visita agendada</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Instituição *</Label>
              <Select value={formData.instituicao_id} onValueChange={v => setFormData(p => ({ ...p, instituicao_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visitador *</Label>
              <Select value={formData.visitador_id} onValueChange={v => setFormData(p => ({ ...p, visitador_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {visitadores.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Formulário <span className="text-slate-400 text-xs">(auto por tipo)</span></Label>
              <Select value={formData.formulario_id} onValueChange={v => setFormData(p => ({ ...p, formulario_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto-selecionar..." />
                </SelectTrigger>
                <SelectContent>
                  {formularios.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nome} ({f.tipo || 'Geral'})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Visita *</Label>
              <Select value={formData.tipo_visita} onValueChange={v => setFormData(p => ({ ...p, tipo_visita: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Presencial">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Presencial
                    </div>
                  </SelectItem>
                  <SelectItem value="Telefónica">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Telefónica
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.data_agendada}
                  onChange={e => setFormData(p => ({ ...p, data_agendada: e.target.value }))}
                />
              </div>
              <div>
                <Label>Hora</Label>
                <Input
                  type="time"
                  value={formData.hora_agendada}
                  onChange={e => setFormData(p => ({ ...p, hora_agendada: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes_agendamento}
                onChange={e => setFormData(p => ({ ...p, observacoes_agendamento: e.target.value }))}
                placeholder="Notas sobre o agendamento..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleEditVisita} className="bg-[#0066CC]">Guardar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Visita</DialogTitle>
            <DialogDescription>
              Tem a certeza que deseja cancelar a visita a "{selectedVisita?.instituicao_nome}"?
              Esta ação pode ser revertida reagendando a visita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>Não, Manter</Button>
            <Button onClick={handleCancelVisita} className="bg-red-600 hover:bg-red-700">
              <XCircle className="w-4 h-4 mr-2" /> Sim, Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reagendar Modal */}
      <Dialog open={reagendarModalOpen} onOpenChange={setReagendarModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reagendar Visita</DialogTitle>
            <DialogDescription>
              Defina uma nova data para a visita a "{selectedVisita?.instituicao_nome}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nova Data *</Label>
                <Input
                  type="date"
                  value={reagendarData.data_agendada}
                  onChange={e => setReagendarData(p => ({ ...p, data_agendada: e.target.value }))}
                />
              </div>
              <div>
                <Label>Hora</Label>
                <Input
                  type="time"
                  value={reagendarData.hora_agendada}
                  onChange={e => setReagendarData(p => ({ ...p, hora_agendada: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Motivo do Reagendamento</Label>
              <Textarea
                value={reagendarData.motivo}
                onChange={e => setReagendarData(p => ({ ...p, motivo: e.target.value }))}
                placeholder="Motivo pelo qual a visita foi reagendada..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReagendarModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleReagendarVisita} className="bg-[#0066CC]">
              <CalendarClock className="w-4 h-4 mr-2" /> Reagendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
